import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import PanelFooter from '_lex/_atoms/PanelFooter';
import { neutral } from '_lex/_foundations/phxColors';
import { LexipolLogo, LexipolLogoWithType } from '_lex/_foundations/img/Logos';
import { Drawer, SecondaryDrawer, NavItemButton, NavItemButtonNoLink, SecondaryNavItemButton } from './styles';
import { OpenInNew } from '@mui/icons-material';

export type NavItem = {
  key: string;
  icon?: React.ReactNode;
  label?: string;
  subtitle?: string;
  divider?: boolean;
  href?: string;
  path?: string;
  children?: NavItem[];
  newWindow?: boolean;
  permissions?: string[];
  appPermissions?: string[];
  featurePermission?: string;
  requiresRiskPool?: boolean;
  sameOrg?: boolean;
  orgContext?: boolean;
  rootOnly?: boolean;
  nonRoot?: boolean;
  alwaysShow?: boolean;
};

interface SidebarProps {
  items: NavItem[];
  onNavigate?: (item: NavItem) => void;
}

// Delay in ms before collapsing the menu when mouse leaves
const COLLAPSE_DELAY_MS = 300;

const Sidebar: React.FC<SidebarProps> = ({ items, onNavigate }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Filter out items that have no href and no children
  const visibleItems = React.useMemo(() => {
    const filtered = items.filter((item) => {
      // Always show items with alwaysShow flag
      if (item.alwaysShow) {
        return true;
      }

      const hasHref = item.href && item.href !== '';
      const hasChildren = item.children && item.children.length > 0;

      // If item has an href (non-empty), show it
      if (hasHref) {
        return true;
      }
      // If item has children (non-empty array), show it
      if (hasChildren) {
        return true;
      }
      // Otherwise, hide it (no href and no children)
      return false;
    });

    return filtered;
  }, [items]);

  const [isOpen, setIsOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [expandedPrimaryItem, setExpandedPrimaryItem] = React.useState<NavItem | null>(null);
  const [activeItemKey, setActiveItemKey] = React.useState<string | null>(null);
  const [activeParentKey, setActiveParentKey] = React.useState<string | null>(null);
  const collapseTimerRef = React.useRef<NodeJS.Timeout>();
  const [isMouseInSidebar, setIsMouseInSidebar] = React.useState(false);
  const [focusedItemKey, setFocusedItemKey] = React.useState<string | null>(null);
  const [focusedSecondaryItemKey, setFocusedSecondaryItemKey] = React.useState<string | null>(null);
  const navRef = React.useRef<HTMLDivElement>(null);
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const navItemsRef = React.useRef<(HTMLAnchorElement | null)[]>([]);
  const secondaryItemsRef = React.useRef<(HTMLAnchorElement | null)[]>([]);

  // Handle window blur - collapse everything
  React.useEffect(() => {
    const handleWindowBlur = () => {
      collapseAll();
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  // Handle document visibility change
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        collapseAll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
      }
    };
  }, []);

  // Sync active state with currentPath
  React.useEffect(() => {
    if (!currentPath) return;

    for (const item of visibleItems) {
      if (item.href && (currentPath === item.href || currentPath.startsWith(`${item.href}/`))) {
        if (item.children?.length) {
          const matchingChild = item.children.find(
            (child) => child.href && (currentPath === child.href || currentPath.startsWith(`${child.href}/`)),
          );
          if (matchingChild) {
            setActiveItemKey(matchingChild.key);
            setActiveParentKey(item.key);
            return;
          }
        }
        setActiveItemKey(item.key);
        setActiveParentKey(null);
        return;
      }

      if (item.children?.length) {
        const matchingChild = item.children.find(
          (child) => child.href && (currentPath === child.href || currentPath.startsWith(`${child.href}/`)),
        );
        if (matchingChild) {
          setActiveItemKey(matchingChild.key);
          setActiveParentKey(item.key);
          return;
        }
      }
    }
  }, [currentPath, visibleItems]);

  const collapseAll = () => {
    setIsOpen(false);
    setIsExpanded(false);
    setExpandedPrimaryItem(null);
    setIsMouseInSidebar(false);
    setFocusedItemKey(null);
    setFocusedSecondaryItemKey(null);
  };

  const startCollapseTimer = () => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
    }
    collapseTimerRef.current = setTimeout(() => {
      collapseAll();
    }, COLLAPSE_DELAY_MS);
  };

  const cancelCollapseTimer = () => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = undefined;
    }
  };

  const handleSidebarMouseEnter = () => {
    if (document.hasFocus()) {
      cancelCollapseTimer();
      setIsMouseInSidebar(true);
      setIsOpen(true);
      setIsExpanded(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    setIsMouseInSidebar(false);
    startCollapseTimer();
  };

  const handlePrimaryItemClick = (item: NavItem, event: React.MouseEvent) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      event.preventDefault();
      cancelCollapseTimer();

      if (expandedPrimaryItem?.key === item.key) {
        setExpandedPrimaryItem(null);
      } else {
        setIsOpen(true);
        setIsExpanded(true);
        setExpandedPrimaryItem(item);
      }
    } else {
      setActiveItemKey(item.key);
      setActiveParentKey(null);
      if (onNavigate) {
        onNavigate(item);
      }
      collapseAll();
    }
  };

  const handleSecondaryNavigation = (item: NavItem, parentItem: NavItem) => {
    setActiveItemKey(item.key);
    setActiveParentKey(parentItem.key);
    if (onNavigate) {
      onNavigate(item);
    }
    collapseAll();
  };

  const isItemActive = (item: NavItem): boolean => {
    if (item.key === activeItemKey || item.key === activeParentKey) {
      return true;
    }

    if (currentPath && item.href) {
      if (currentPath === item.href || currentPath.startsWith(`${item.href}/`)) {
        return true;
      }
    }

    if (currentPath && item.children?.length) {
      const hasActiveChild = item.children.some(
        (child) => child.href && (currentPath === child.href || currentPath.startsWith(`${child.href}/`)),
      );
      if (hasActiveChild) {
        return true;
      }
    }

    return false;
  };

  const handleNavFocus = () => {
    cancelCollapseTimer();
  };

  const handleNavBlur = (event: React.FocusEvent) => {
    // Only start collapse timer if focus is leaving the entire navigation
    if (!navRef.current?.contains(event.relatedTarget as Node)) {
      startCollapseTimer();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, item: NavItem, index: number) => {
    const hasChildren = item.children && item.children.length > 0;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (hasChildren) {
          if (expandedPrimaryItem?.key === item.key) {
            setExpandedPrimaryItem(null);
          } else {
            setExpandedPrimaryItem(item);
          }
        } else {
          setActiveItemKey(item.key);
          setActiveParentKey(null);
          if (onNavigate) {
            onNavigate(item);
          }
          collapseAll();

          if (item.href) {
            if (item.newWindow) {
              window.open(item.href, '_blank');
            } else {
              window.location.href = item.href;
            }
          }
        }
        break;
      case 'ArrowRight':
        if (hasChildren) {
          event.preventDefault();
          setExpandedPrimaryItem(item);
          if (item.children && item.children.length > 0) {
            setFocusedSecondaryItemKey(item.children[0].key);
            if (secondaryItemsRef.current.length === 0) {
              secondaryItemsRef.current = item.children.map(() => null);
            }
            setTimeout(() => {
              secondaryItemsRef.current[0]?.focus();
            }, 0);
          }
        }
        break;
      case 'ArrowLeft':
        if (expandedPrimaryItem) {
          event.preventDefault();
          setExpandedPrimaryItem(null);
          setFocusedSecondaryItemKey(null);
          setFocusedItemKey(item.key);
        }
        break;
      case 'Tab':
        if (event.shiftKey) {
          const prevIndex = index - 1;
          if (prevIndex >= 0) {
            event.preventDefault();
            setCurrentTabIndex(prevIndex);
            navItemsRef.current[prevIndex]?.focus();
          }
        } else {
          const nextIndex = index + 1;
          if (nextIndex < visibleItems.length) {
            event.preventDefault();
            setCurrentTabIndex(nextIndex);
            navItemsRef.current[nextIndex]?.focus();
          } else if (expandedPrimaryItem && expandedPrimaryItem.children && expandedPrimaryItem.children.length > 0) {
            // If secondary menu is open, move focus to first secondary item
            event.preventDefault();
            setFocusedSecondaryItemKey(expandedPrimaryItem.children[0].key);
            setTimeout(() => {
              secondaryItemsRef.current[0]?.focus();
            }, 0);
          }
        }
        break;
    }
  };

  const handleSecondaryKeyDown = (event: React.KeyboardEvent, item: NavItem, parentItem: NavItem, index: number) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setActiveItemKey(item.key);
        setActiveParentKey(parentItem.key);
        collapseAll();

        if (item.href) {
          if (item.newWindow) {
            window.open(item.href, '_blank');
          } else {
            window.location.href = item.href;
          }
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        setExpandedPrimaryItem(null);
        setFocusedSecondaryItemKey(null);
        const parentIndex = visibleItems.findIndex((i) => i.key === parentItem.key);
        if (parentIndex !== -1) {
          setCurrentTabIndex(parentIndex);
          navItemsRef.current[parentIndex]?.focus();
        }
        break;
      case 'Tab':
        if (event.shiftKey) {
          const prevIndex = index - 1;
          if (prevIndex >= 0) {
            event.preventDefault();
            secondaryItemsRef.current[prevIndex]?.focus();
          } else {
            event.preventDefault();
            const parentIdx = visibleItems.findIndex((i) => i.key === parentItem.key);
            if (parentIdx !== -1) {
              setCurrentTabIndex(parentIdx);
              navItemsRef.current[parentIdx]?.focus();
            }
          }
        } else {
          const nextIndex = index + 1;
          if (nextIndex < (parentItem.children?.length || 0)) {
            event.preventDefault();
            secondaryItemsRef.current[nextIndex]?.focus();
          }
        }
        break;
    }
  };

  const renderNavItem = (item: NavItem, index: number, showDivider = false) => {
    const isActive = isItemActive(item);
    const hasChildren = item.children && item.children.length > 0;
    const isPrimaryExpanded = expandedPrimaryItem?.key === item.key;
    const isExternal =
      item.href?.startsWith('http://') ||
      item.href?.startsWith('https://') ||
      (item.href && !item.href.startsWith('/admin') && item.href.startsWith('/'));
    const shouldRenderAsLink = !hasChildren && !!item.href;
    const ButtonComponent = shouldRenderAsLink ? NavItemButton : NavItemButtonNoLink;

    return (
      <Box
        key={item.key}
        sx={{ width: '100%', px: 3, pt: 1 }}
      >
        {showDivider && <Divider sx={{ borderColor: neutral[500], mb: 1 }} />}
        <ButtonComponent
          ref={(el: HTMLAnchorElement | null) => (navItemsRef.current[index] = el)}
          className={`sidebar__item ${isActive ? 'active' : ''}`}
          data-test={`nav-${item.key}`}
          {...(shouldRenderAsLink && isExternal ? { component: 'a' as any } : {})}
          {...(!shouldRenderAsLink ? { component: 'button' as any } : {})}
          onClick={(e: React.MouseEvent) => {
            if (hasChildren || !isExternal) {
              handlePrimaryItemClick(item, e);
            }
          }}
          onKeyDown={(e) => handleKeyDown(e, item, index)}
          onFocus={() => setFocusedItemKey(item.key)}
          tabIndex={currentTabIndex === index ? 0 : -1}
          role='menuitem'
          aria-haspopup={hasChildren ? 'true' : undefined}
          aria-expanded={isPrimaryExpanded}
          {...(shouldRenderAsLink ? { href: item.href } : {})}
          target={item.newWindow && shouldRenderAsLink ? '_blank' : undefined}
          sx={{ display: 'inline-flex' }}
        >
          <Box
            className='sidebar__item-content'
            sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                className='sidebar__item-icon'
                sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}
              >
                {item.icon && (
                  <Box
                    className='sidebar__item-icon-wrapper'
                    sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {item.icon}
                  </Box>
                )}
              </Box>
              {isExpanded && item.label && (
                <Typography
                  className='sidebar__item-label'
                  variant='bodyMd'
                  sx={{ color: neutral[25], whiteSpace: 'nowrap' }}
                >
                  {item.label}
                </Typography>
              )}
            </Box>
            {isExpanded && item.newWindow && <OpenInNew sx={{ fontSize: 16, color: neutral[400] }} />}
          </Box>
        </ButtonComponent>
      </Box>
    );
  };

  React.useEffect(() => {
    navItemsRef.current = visibleItems.map(() => null);
  }, [visibleItems]);

  return (
    <Box
      ref={navRef}
      className='sidebar'
      role='navigation'
      aria-label='Main navigation'
      sx={{
        height: 'calc(100% - 64px)',
        position: 'fixed',
        left: 0,
        top: 64,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        display: 'flex',
        flexDirection: 'row',
      }}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
      onFocus={handleNavFocus}
      onBlur={handleNavBlur}
      tabIndex={-1}
    >
      <Drawer
        variant='permanent'
        open={isOpen}
        className='sidebar__drawer'
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '& .MuiDrawer-paper': {
            transition: (theme) =>
              theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.easeInOut,
                duration: 800,
              }),
          },
        }}
      >
        <Box
          className='sidebar__content'
          sx={{ py: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        >
          <Stack
            className='sidebar__items'
            role='menu'
          >
            {visibleItems.filter((item) => !item.divider).map((item, index) => renderNavItem(item, index))}
          </Stack>
          <Stack
            className='sidebar__bottom-items'
            role='menu'
          >
            {visibleItems
              .filter((item) => item.divider)
              .map((item) => {
                const actualIndex = visibleItems.findIndex((i) => i.key === item.key);
                return renderNavItem(item, actualIndex, true);
              })}
          </Stack>
        </Box>
        <PanelFooter
          leftItems={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isExpanded ? 'start' : 'center',
                width: '100%',
              }}
            >
              {isExpanded ? (
                <Box sx={{ display: 'flex', justifyContent: 'start', width: '100%' }}>
                  <LexipolLogoWithType />
                </Box>
              ) : (
                <Box
                  sx={{
                    height: 24,
                    width: 21,
                  }}
                >
                  <LexipolLogo />
                </Box>
              )}
            </Box>
          }
          sx={{
            backgroundColor: 'transparent !important',
            borderTop: `1px solid ${neutral[500]} !important`,
          }}
        />
      </Drawer>
      <SecondaryDrawer
        variant='permanent'
        open={!!expandedPrimaryItem?.children?.length}
        className='sidebar__secondary-drawer'
      >
        <Box
          className='sidebar__secondary-content'
          role='menu'
          aria-label={`${expandedPrimaryItem?.label} submenu`}
          sx={{
            py: 1,
            height: '100%',
          }}
        >
          <Box
            className='secondary-nav-content'
            sx={{ height: '100%' }}
          >
            {expandedPrimaryItem &&
              expandedPrimaryItem.label &&
              !expandedPrimaryItem.children?.some((child) => child.divider) && (
                <Typography
                  variant='eyebrowSm'
                  sx={{ padding: '24px 16px', color: neutral[500] }}
                >
                  {expandedPrimaryItem.label}
                </Typography>
              )}
            <Stack spacing={3}>
              {expandedPrimaryItem?.children?.map((item, index) => {
                // Render divider as section header with divider line below
                if (item.divider) {
                  return (
                    <Box
                      key={item.key}
                      sx={{ width: '100%', px: 3 }}
                    >
                      <Typography
                        variant='bodyXs'
                        sx={{
                          padding: '24px 0px 6px 6px',
                          color: neutral[500],
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Divider sx={{ borderColor: neutral[300], mt: 0.5, mb: 0 }} />
                    </Box>
                  );
                }

                const isActive = isItemActive(item);
                // External URLs: absolute URLs (http/https) or paths that don't start with /admin (routed to Flutter app via CloudFront)
                const isExternal =
                  item.href?.startsWith('http://') ||
                  item.href?.startsWith('https://') ||
                  (item.href && !item.href.startsWith('/admin') && item.href.startsWith('/'));
                return (
                  <Box
                    key={item.key}
                    sx={{ width: '100%', px: 3 }}
                  >
                    <SecondaryNavItemButton
                      ref={(el: HTMLAnchorElement | null) => (secondaryItemsRef.current[index] = el)}
                      key={item.key}
                      className={isActive ? 'active' : ''}
                      data-test={`nav-${expandedPrimaryItem.key}-${item.key}`}
                      {...(isExternal ? { component: 'a' as any } : {})}
                      onClick={() => {
                        if (!isExternal && expandedPrimaryItem) {
                          handleSecondaryNavigation(item, expandedPrimaryItem);
                        }
                      }}
                      onKeyDown={(e) => handleSecondaryKeyDown(e, item, expandedPrimaryItem, index)}
                      onFocus={() => setFocusedSecondaryItemKey(item.key)}
                      tabIndex={focusedSecondaryItemKey === item.key ? 0 : -1}
                      role='menuitem'
                      href={item.href || undefined}
                      target={item.newWindow ? '_blank' : undefined}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                      >
                        <span>{item.label}</span>
                        {item.newWindow && (
                          <OpenInNew
                            sx={{
                              fontSize: 16,
                              color: neutral[400],
                            }}
                          />
                        )}
                      </Box>
                      {item.subtitle && (
                        <Typography
                          variant='caption'
                          sx={{
                            color: neutral[400],
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            fontWeight: 400,
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}
                    </SecondaryNavItemButton>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </SecondaryDrawer>
    </Box>
  );
};

export default Sidebar;
